// ABOUTME: Scroll-reveal + scroll-coupled zoom engine for the Jocko motion system (DS-005).
// IntersectionObserver drives .scroll-trigger reveals with the live 75ms cascade; the paired
// tokens and keyframes live in assets/jf-brand.css. Horizon ships no native scroll-reveal
// setting, so this reproduces the live behavior (themes/dtc assets/animations.js) as a module.

import { prefersReducedMotion, onDocumentReady } from '@theme/utilities';

const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

/**
 * Reveals intersecting elements and stamps the cascade order.
 * The order index is the element's position within the observer callback batch, matching live.
 * @param {IntersectionObserverEntry[]} elements
 * @param {IntersectionObserver} observer
 */
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade')) {
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
        }
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

/**
 * Observes every .scroll-trigger under rootEl. Safe to re-run after section hydration —
 * observed elements are unobserved once revealed.
 * @param {ParentNode} [rootEl]
 * @param {boolean} [isDesignModeEvent]
 */
export function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(
    rootEl.querySelectorAll(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`)
  );
  if (animationTriggerElements.length === 0) return;

  // In the editor everything is shown immediately — reveals would hide freshly edited content.
  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

/**
 * Couples --zoom-in-ratio on .animate--zoom-in elements to scroll position (0.2% over the pass).
 * @param {ParentNode} [rootEl]
 */
export function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (prefersReducedMotion()) return;

  const animationTriggerElements = Array.from(
    rootEl.querySelectorAll(`.${SCROLL_ZOOM_IN_TRIGGER_CLASSNAME}`)
  );
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', `${1 + scaleAmount * percentageSeen(element)}`);

    // Live wraps this in throttle() but passes no delay, so it never actually throttles and the
    // ratio updates every scroll event. Reproduced directly: routing it through Horizon's
    // throttle() the same way would compare against an undefined delay and never fire at all.
    window.addEventListener(
      'scroll',
      () => {
        if (!elementIsVisible) return;
        element.style.setProperty(
          '--zoom-in-ratio',
          `${1 + scaleAmount * percentageSeen(element)}`
        );
      },
      { passive: true }
    );
  });
}

/**
 * How far the element has travelled through the viewport, 0-100.
 * @param {HTMLElement} element
 * @returns {number}
 */
function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the element yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the element
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

onDocumentReady(() => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) =>
    initializeScrollAnimationTrigger(event.target, true)
  );
  document.addEventListener('shopify:section:reorder', () =>
    initializeScrollAnimationTrigger(document, true)
  );
}
